/**
 * Product
 *
 * @module product
 */
import { CACHE_TTL_MS, syncVariantAvailability } from '../utils/inventory';

const component = {

    /**
     * Init component
     * 
     * @returns {undefined}
     */
    init: () => {

        /**
         * Product module api
         * 
         * @global
         */
        window.theme.product = component;

        component.initCurrencyChangeListener();
    },

    /**
     * Initialize listener for currency/localization changes.
     * 
     * @private
     * @method
     * @name initCurrencyChangeListener
     * @returns {undefined}
     */
    initCurrencyChangeListener: () => {
        document.addEventListener('submit', (event) => {
            const form = event.target;
            if (form && form.classList.contains('shopify-localization-form')) {
                component.clearCache();
            }
        });

        const currencySelectors = document.querySelectorAll('.country-selector, [name="country_code"]');
        currencySelectors.forEach(selector => {
            selector.addEventListener('change', () => {
                component.clearCache();
            });
        });

        if (window.Shopify && window.Shopify.currency) {
            let lastCurrency = window.Shopify.currency.active;
            
            setInterval(() => {
                if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
                    const currentCurrency = window.Shopify.currency.active;
                    if (currentCurrency !== lastCurrency) {
                        lastCurrency = currentCurrency;
                        component.clearCache();
                    }
                }
            }, 500);
        }
    },

    /**
     * Load product object by handle.
     * 
     * @public
     * @method
     * @name loadProduct
     * @todo Implement a test for this function.
     * @param {string} handle Product handle.
     * @param {string} extended Extend product data.
     * @returns {Promise} Promise object represents a product.
     */
    loadProduct: (handle, extended) => {
        
        return new Promise((resolve, reject) => {
            
            let cache = component.getCache(handle);
        
            if (cache) {

                if (!cache.extended && extended) {

                    component.request(handle, extended).then(product => {
                        component.resolveProduct(product, resolve);
                    });

                } else {
                
                    component.resolveProduct(cache, resolve);
                }

            } else {
                
                component.request(handle, extended).then(product => {

                    component.resolveProduct(product, resolve);
                });
            }

        });
    },

    /**
     * Sync variant availability and resolve loadProduct promise.
     *
     * @private
     * @param {object} product Product data.
     * @param {Function} resolve Promise resolve callback.
     * @returns {undefined}
     */
    resolveProduct: (product, resolve) => {
        syncVariantAvailability(product);
        resolve(product);
    },

    /**
     * Make a request to get product data.
     * 
     * @public
     * @method
     * @name request
     * @todo Implement a test for this function.
     * @param {string} handle Product handle.
     * @param {string} extended Extend product data.
     * @returns {Promise} Promise object represents a product.
     */
    request: (handle, extended) => {

        return new Promise((resolve, reject) => {

            let url = window.Shopify.routes.root + (extended) ? '/products/' + handle + '?view=json' : '/products/' + handle + '.json';

            fetch(url).then(resp => {

                return resp.json();
    
            }).then(data => {
    
                if (data.product) {

                    data = component.normalizeData(data);

                    component.cache(data.product);

                    resolve(data.product);

                } else {

                    reject(data);
                }
            }).catch(function(error) {
                console.log(error);
                reject(error);
            });
        });
    },

    /**
     * Normalize product data.
     * 
     * @private
     * @method
     * @name normalizeData
     * @todo Implement a test for this function.
     * @param {object} data Request data.
     * @returns {object} Normalized data.
     */
    normalizeData: data => {

        // Fix options
        if (data.options_with_values) {
            data.product.options = data.options_with_values;
        }

        // Add options by name
        if (data.options_by_name) {
            data.product.options_by_name = data.options_by_name;
        }

        // Add extended flag
        if (data.extended) {
            data.product.extended = true;
        }

        // Fix images
        if (data.product.extended) {
            data.product.images = data.product.media;
        }

        return data;
    },

    /**
     * Check if a product has variants.
     * 
     * @public
     * @method
     * @name hasVariants
     * @param {object} product Product data.
     * @returns {boolean} Product has variants or not.
     */
    hasVariants: product => {

        if(product.options.length == 1 && product.options[0].values.length == 1) {

            return false;

        } else {

            return true;
        }
    },

    /**
     * Chace a product data.
     * 
     * @private
     * @method
     * @name request
     * @param {object} product Product data.
     * @returns {boolean}
     */
    cache: product => {

        let productValue = JSON.stringify(product);

        if (product.handle && productValue.length < 50000) {

            try {
                const cacheData = {
                    product: product,
                    currency: window.Shopify?.currency?.active || window.Shopify?.shop?.currency || 'USD',
                    timestamp: Date.now()
                };

                sessionStorage.setItem(product.handle, JSON.stringify(cacheData));
    
                return true;
                
            } catch (error) {
                
                return false;
            }

        } else {

            return false;
        }
    },

    /**
     * Get product from cache.
     * 
     * @private
     * @method
     * @name request
     * @param {string} handle Product handle.
     * @param {string} extended Extend product data.
     * @returns {Promise} Promise object represents a product.
     */
    getCache: handle => {
        try {
            const cached = sessionStorage.getItem(handle);
            if (!cached) {
                return null;
            }

            const cacheData = JSON.parse(cached);
            
            if (cacheData.product) {
                const currentCurrency = window.Shopify?.currency?.active || window.Shopify?.shop?.currency || 'USD';
                if (cacheData.currency !== currentCurrency) {
                    sessionStorage.removeItem(handle);
                    return null;
                }

                if (cacheData.timestamp && Date.now() - cacheData.timestamp > CACHE_TTL_MS) {
                    sessionStorage.removeItem(handle);
                    return null;
                }

                if (window.productUpdatedAt && cacheData.product.updated_at
                    && cacheData.product.updated_at !== window.productUpdatedAt) {
                    sessionStorage.removeItem(handle);
                    return null;
                }

                return cacheData.product;
            } else {
                if (cacheData.handle && cacheData.variants) {
                    return cacheData;
                }
                return null;
            }
        } catch (error) {
            return null;
        }
    },

    /**
     * Clear product cache for a specific handle.
     * 
     * @public
     * @method
     * @name clearCache
     * @param {string} handle Product handle. If not provided, clears all product caches.
     * @returns {boolean} Success status.
     */
    clearCache: (handle) => {
        try {
            if (handle) {
                sessionStorage.removeItem(handle);
                return true;
            } else {
                const keys = Object.keys(sessionStorage);
                keys.forEach(key => {
                    if (key && typeof key === 'string' && key.length > 0 && !key.startsWith('_')) {
                        try {
                            const cached = JSON.parse(sessionStorage.getItem(key));
                            if (cached && ((cached.handle && cached.variants) || (cached.product && cached.product.handle))) {
                                sessionStorage.removeItem(key);
                            }
                        } catch (e) {
                        }
                    }
                });
                return true;
            }
        } catch (error) {
            console.error('Error clearing product cache:', error);
            return false;
        }
    },

    /**
     * Get variant data
     * 
     * @public
     * @method
     * @name getVariant
     * @param {integer} variantId Variant id.
     * @param {object} product Product data.
     * @returns {object} Variant data.
     */
    getVariant: (variantId, product) => {

        let variantData = null;

        product.variants.forEach(variant => {

            if (variant.id == variantId)
                variantData = variant;
        });

        return variantData;
    },

    /**
     * Get a loaded product from cache with synced availability.
     *
     * @public
     * @method
     * @name getLoadedProduct
     * @param {string} handle Product handle.
     * @returns {object|null} Product data.
     */
    getLoadedProduct: (handle) => {
        const product = component.getCache(handle);

        if (product) {
            syncVariantAvailability(product);
        }

        return product;
    }
};

export default component;
